import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFlag, faCheck, faTrash, faList } from "@fortawesome/free-solid-svg-icons";
import {
  Badge,
  Button,
  ButtonGroup,
  Card,
  Form,
  Modal,
  Spinner,
  Table,
} from "@themesberg/react-bootstrap";
import { GetApiRequest, PostApiRequest } from "../services/api";
import { toast } from "../components/Toaster";
import { Routes } from "../routes";

const TYPE_LABELS = {
  content: "Post",
  comment: "Comment",
  user: "User",
  review: "Review",
  experience: "Experience",
};

const REASON_LABELS = {
  spam: "Spam",
  harassment: "Harassment",
  hate: "Hate",
  sexual: "Sexual",
  violence: "Violence",
  self_harm: "Self-harm",
  illegal: "Illegal",
  misinformation: "Misinformation",
  impersonation: "Impersonation",
  intellectual_property: "IP / copyright",
  other: "Other",
};

/** What "Remove" does for each target type — shown on the button and confirm. */
const REMOVE_LABELS = {
  content: "Remove post",
  comment: "Remove comment",
  user: "Deactivate user",
  review: "Hide review",
  experience: "Delete experience",
};

function targetLink(group) {
  const t = group.target;
  switch (group.targetType) {
    case "content":
      return Routes.ContentDetails.path.replace(":id", group.targetId);
    case "comment":
      return t?.contentId ? Routes.ContentDetails.path.replace(":id", t.contentId) : null;
    case "user":
      return Routes.UserDetails.path.replace(":userId", group.targetId);
    case "experience":
      return Routes.BookDetails.path.replace(":id", group.targetId);
    case "review":
      return t?.experienceId ? Routes.BookDetails.path.replace(":id", t.experienceId) : null;
    default:
      return null;
  }
}

function targetSummary(group) {
  const t = group.target;
  if (!t) return <span className="text-muted">(no longer exists)</span>;
  switch (group.targetType) {
    case "content":
      return (
        <>
          {t.description || <em>No caption</em>}
          <div className="small text-muted">by {t.creator?.name || "unknown"}</div>
          {t.status !== "ACTIVE" && (
            <Badge bg={t.status === "HIDDEN_PENDING_REVIEW" ? "warning" : "secondary"}>
              {t.status === "HIDDEN_PENDING_REVIEW" ? "Auto-hidden" : t.status}
            </Badge>
          )}
        </>
      );
    case "comment":
      return (
        <>
          “{t.body}”
          <div className="small text-muted">by {t.user?.name || "unknown"}</div>
          {t.status !== "ACTIVE" && (
            <Badge bg={t.status === "HIDDEN_PENDING_REVIEW" ? "warning" : "secondary"}>
              {t.status === "HIDDEN_PENDING_REVIEW" ? "Auto-hidden" : "Removed"}
            </Badge>
          )}
        </>
      );
    case "user":
      return (
        <>
          {t.name || <em>No name</em>}
          {t.bio && <div className="small text-muted">{t.bio}</div>}
          {!t.isActive && <Badge bg="secondary">Deactivated</Badge>}
        </>
      );
    case "review":
      return (
        <>
          {"★".repeat(t.rating || 0)} {t.review || <em>No text</em>}
          <div className="small text-muted">by {t.user?.name || "unknown"}</div>
          {!t.isActive && <Badge bg="secondary">Hidden</Badge>}
        </>
      );
    case "experience":
      return (
        <>
          {t.title}
          {t.state !== "active" && <Badge bg="secondary">{t.state}</Badge>}
        </>
      );
    default:
      return null;
  }
}

const formatDate = (iso) => (iso ? new Date(iso).toLocaleString() : "");

const Reports = () => {
  const [status, setStatus] = useState("open");
  const [targetType, setTargetType] = useState("");
  const [page, setPage] = useState(1);
  const [groups, setGroups] = useState([]);
  const [pagination, setPagination] = useState({ pages: 1, total: 0 });
  const [loading, setLoading] = useState(false);

  const [confirm, setConfirm] = useState(null); // { group, action }
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [details, setDetails] = useState(null); // { group, reports, loading }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await GetApiRequest("admin/reports", {
        status,
        page,
        limit: 20,
        ...(targetType && { targetType }),
      });
      setGroups(res.data.data.groups);
      setPagination(res.data.data.pagination);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load reports");
    } finally {
      setLoading(false);
    }
  }, [status, targetType, page]);

  useEffect(() => {
    load();
  }, [load]);

  const openDetails = async (group) => {
    setDetails({ group, reports: [], loading: true });
    try {
      const res = await GetApiRequest(`admin/reports/${group.targetType}/${group.targetId}`);
      setDetails({ group, reports: res.data.data.reports, loading: false });
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load report details");
      setDetails(null);
    }
  };

  const resolve = async () => {
    const { group, action } = confirm;
    setSubmitting(true);
    try {
      await PostApiRequest(`admin/reports/${group.targetType}/${group.targetId}/resolve`, {
        action,
        ...(note.trim() && { note: note.trim() }),
      });
      toast.success(action === "remove" ? "Removed and reports closed" : "Reports dismissed");
      setConfirm(null);
      setNote("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to resolve reports");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap align-items-center py-4">
        <div>
          <h4>
            <FontAwesomeIcon icon={faFlag} className="me-2" />
            Reports
          </h4>
          <p className="mb-0 text-muted">
            User reports of posts, comments, profiles, reviews and experiences. Aim to act within 24 hours.
          </p>
        </div>
        <div className="d-flex gap-2 mt-2 mt-md-0">
          <ButtonGroup>
            {["open", "actioned", "dismissed"].map((s) => (
              <Button
                key={s}
                variant={status === s ? "primary" : "outline-primary"}
                size="sm"
                onClick={() => {
                  setStatus(s);
                  setPage(1);
                }}
              >
                {s[0].toUpperCase() + s.slice(1)}
              </Button>
            ))}
          </ButtonGroup>
          <Form.Select
            size="sm"
            value={targetType}
            onChange={(e) => {
              setTargetType(e.target.value);
              setPage(1);
            }}
            style={{ width: 160 }}
            aria-label="Filter by type"
          >
            <option value="">All types</option>
            {Object.entries(TYPE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Form.Select>
        </div>
      </div>

      <Card border="light" className="shadow-sm">
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          ) : groups.length === 0 ? (
            <div className="text-center py-5 text-muted">
              {status === "open" ? "No open reports. 🎉" : `No ${status} reports.`}
            </div>
          ) : (
            <Table responsive hover className="align-items-center mb-0">
              <thead className="thead-light">
                <tr>
                  <th>Type</th>
                  <th>Reported item</th>
                  <th>Reasons</th>
                  <th>Reports</th>
                  <th>Last reported</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => {
                  const link = targetLink(g);
                  return (
                    <tr key={`${g.targetType}:${g.targetId}`}>
                      <td>
                        <Badge bg="dark">{TYPE_LABELS[g.targetType]}</Badge>
                      </td>
                      <td style={{ maxWidth: 360, whiteSpace: "normal" }}>
                        {link ? <Link to={link}>{targetSummary(g)}</Link> : targetSummary(g)}
                      </td>
                      <td style={{ whiteSpace: "normal" }}>
                        {Object.entries(g.reasons).map(([reason, n]) => (
                          <Badge key={reason} bg="danger" className="me-1 mb-1">
                            {REASON_LABELS[reason] || reason}
                            {n > 1 ? ` ×${n}` : ""}
                          </Badge>
                        ))}
                      </td>
                      <td>{g.reportCount}</td>
                      <td className="small">{formatDate(g.lastReportedAt)}</td>
                      <td className="text-end text-nowrap">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="me-1"
                          onClick={() => openDetails(g)}
                          title="See individual reports"
                        >
                          <FontAwesomeIcon icon={faList} />
                        </Button>
                        {status === "open" && (
                          <>
                            <Button
                              variant="outline-success"
                              size="sm"
                              className="me-1"
                              onClick={() => setConfirm({ group: g, action: "dismiss" })}
                            >
                              <FontAwesomeIcon icon={faCheck} className="me-1" />
                              Dismiss
                            </Button>
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => setConfirm({ group: g, action: "remove" })}
                            >
                              <FontAwesomeIcon icon={faTrash} className="me-1" />
                              {REMOVE_LABELS[g.targetType]}
                            </Button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>
        {pagination.pages > 1 && (
          <Card.Footer className="d-flex justify-content-between align-items-center">
            <small className="text-muted">
              Page {page} of {pagination.pages} · {pagination.total} items
            </small>
            <ButtonGroup>
              <Button
                size="sm"
                variant="outline-primary"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="outline-primary"
                disabled={page >= pagination.pages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </ButtonGroup>
          </Card.Footer>
        )}
      </Card>

      {/* Confirm dismiss / remove */}
      <Modal show={!!confirm} onHide={() => !submitting && setConfirm(null)} centered>
        {confirm && (
          <>
            <Modal.Header closeButton>
              <Modal.Title className="h6">
                {confirm.action === "remove"
                  ? `${REMOVE_LABELS[confirm.group.targetType]}?`
                  : "Dismiss reports?"}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p>
                {confirm.action === "remove"
                  ? {
                      content: "The post is removed from GoLocal for everyone.",
                      comment: "The comment is removed for everyone.",
                      user: "The user is deactivated and can no longer use the app.",
                      review: "The review is hidden for everyone.",
                      experience: "The experience listing is deleted.",
                    }[confirm.group.targetType]
                  : confirm.group.target?.status === "HIDDEN_PENDING_REVIEW"
                    ? `No violation — the auto-hidden ${TYPE_LABELS[confirm.group.targetType].toLowerCase()} is restored for everyone (it stays hidden for people who reported it).`
                    : "No violation — the item stays up."}{" "}
                All {confirm.group.reportCount} open report
                {confirm.group.reportCount === 1 ? "" : "s"} will be closed.
              </p>
              <Form.Group>
                <Form.Label className="small">Note (optional, internal)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  maxLength={500}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setConfirm(null)} disabled={submitting}>
                Cancel
              </Button>
              <Button
                variant={confirm.action === "remove" ? "danger" : "success"}
                onClick={resolve}
                disabled={submitting}
              >
                {submitting ? (
                  <Spinner size="sm" animation="border" />
                ) : confirm.action === "remove" ? (
                  REMOVE_LABELS[confirm.group.targetType]
                ) : (
                  "Dismiss"
                )}
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>

      {/* Individual reports */}
      <Modal show={!!details} onHide={() => setDetails(null)} size="lg" centered>
        {details && (
          <>
            <Modal.Header closeButton>
              <Modal.Title className="h6">
                Reports on this {TYPE_LABELS[details.group.targetType].toLowerCase()}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {details.loading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" />
                </div>
              ) : (
                <Table size="sm" responsive>
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Reporter</th>
                      <th>Reason</th>
                      <th>Details</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {details.reports.map((r) => (
                      <tr key={r.id}>
                        <td className="small text-nowrap">{formatDate(r.createdAt)}</td>
                        <td className="small">
                          {r.reporter ? (
                            <Link to={Routes.UserDetails.path.replace(":userId", r.reporter.id)}>
                              {r.reporter.name || r.reporter.email || "user"}
                            </Link>
                          ) : (
                            <span className="text-muted">deleted user</span>
                          )}
                        </td>
                        <td>
                          <Badge bg="danger">{REASON_LABELS[r.reason] || r.reason}</Badge>
                        </td>
                        <td className="small" style={{ whiteSpace: "normal" }}>
                          {r.details || <span className="text-muted">—</span>}
                        </td>
                        <td className="small">
                          {r.status}
                          {r.resolver && <div className="text-muted">by {r.resolver.name}</div>}
                          {r.resolutionNote && (
                            <div className="text-muted">“{r.resolutionNote}”</div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Modal.Body>
          </>
        )}
      </Modal>
    </>
  );
};

export default Reports;
